<template>
  <v-expansion-panel
    data-component-id="Application"
    :readonly="isEditMode"
    style="border: 1px solid #d7d7d7; border-radius: 10px; background-color: transparent;"
  >
    <v-expansion-panel-header
      class="flex-row-reverse"
      style="gap: 16px;"
      color="grey lighten-2"
      v-bind="
        hideExpandIcon ? { 'expand-icon': '' } : {}
      "
    >
      <section
        class="
          d-flex justify-end
          text-right
        "
        style="gap: 16px;"
      >
        <v-btn
          v-if="!isEditMode"
          icon
          :disabled="permission ? !canPerformAction(`EDIT_${permission}`) : false"
          @click.stop="setEditMode(true)"
        >
          <v-icon class="justify-end">
            mdi-pencil
          </v-icon>
        </v-btn>
        <v-btn
          v-if="!isCreateMode && isEditMode"
          :disabled="permission ? !canPerformAction(`EDIT_${permission}`) : false"
          @click.stop="handleCancelAction"
        >
          <span class="d-none d-md-block">Annuler les modifications</span>
          <v-icon class="d-block d-md-none">
            mdi-refresh
          </v-icon>
        </v-btn>
        <v-btn
          v-if="isEditMode"
          :disabled="(permission ? !canPerformAction(`EDIT_${permission}`) : false) || !isValidForm"
          color="primary"
          @click.stop="saveChanges"
        >
          <span class="d-none d-md-block">Enregistrer</span>
          <v-icon class="d-block d-md-none">
            mdi-content-save
          </v-icon>
        </v-btn>
      </section>
      <h3>{{ title }}</h3>
    </v-expansion-panel-header>

    <v-expansion-panel-content
      class="pa-5"
      color="transparent"
      style="border-bottom-right-radius: 10px; border-bottom-left-radius: 10px;"
    >
      <v-list
        dense
        class="px-5"
        style="border-radius: 10px;"
      >
        <v-form
          ref="application-form"
          class="d-flex flex-wrap"
          style="gap: 16px;"
        >
          <v-row>
            <template v-for="(field, fieldIndex) in fields">
              <v-col
                v-if="!field.hidden"
                :key="fieldIndex"
                :cols="12"
                :md="field.fullWidth ? 12 : 6"
              >
                <v-list-item
                  v-if="!field.hidden"
                  :key="fieldIndex"
                  class="px-0 px-md-4"
                >
                  <!-- :style="field.fullWidth ? '' : 'min-width: 49%; max-width: 49%'" -->
                  <v-list-item-icon>
                    <v-icon :color="getFormattedValue(field) === 'N/A' ? 'grey' : 'primary'">
                      mdi-{{ field.icon }}
                    </v-icon>
                  </v-list-item-icon>

                  <v-list-item-content
                    class="
                      align-self-baseline
                      overflow-visible
                    "
                  >
                    <v-skeleton-loader
                      :loading="isLoading"
                      type="list-item"
                    >
                      <v-list-item-title
                        class="overflow-visible break-word"
                      >
                        <slot
                          v-if="isEditMode"
                          :name="field.name"
                          :field="field"
                          :value="data[field.name]"
                        >
                          <component
                            :is="field.component ?? 'GenericField'"
                            v-if="field.viewType !== 'Switch'"
                            :id="field.name"
                            v-model="data[field.name]"
                            :name="field.name"
                            :items="getFieldReferences(field.optionsByReference)"
                            :input="field"
                            :rules="field.rules"
                            :data="application"
                            :prepend-icon="''"
                            :field="field"
                            :classes="getInputClasses(field)"
                            @change="(value) => handleChanges(field, value)"
                            @input="(value) => handleChanges(field, value)"
                          />
                          <v-switch
                            v-else
                            :id="field.name"
                            :input-value="data[field.name]"
                            :label="field.label"
                            :required="field.required"
                            :false-value="false"
                            class="
                              switch-flex-column
                              mt-0
                            "
                            @input="(value) => handleChanges(field, value)"
                            @change="(value) => handleChanges(field, value)"
                          />
                        </slot>
                        <span
                          v-else-if="getFormattedValue(field) !== 'N/A'"
                          class="body-1 font-family-saira font-weight-regular"
                        >
                          <a v-if="['email', 'mobilephone'].includes(field.name)" class="text-decoration-none text-primary" :href="`${field.name === 'email' ? 'mailto' : 'tel'}:${getFormattedValue(field)}`">{{ getFormattedValue(field) }}</a>
                          <span v-else>{{ getFormattedValue(field) }}</span>
                        </span>
                        <span
                          v-else-if="getFormattedValue(field) === 'N/A'"
                          class="body-1 font-family-saira font-weight-regular text--secondary"
                        >
                          {{ getFormattedValue(field) }}
                        </span>
                      </v-list-item-title>
                    </v-skeleton-loader>
                    <v-list-item-subtitle
                      v-if="!isEditMode"
                    >
                      {{ field.label }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </template>
          </v-row>
        </v-form>
      </v-list>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import moment from 'moment';

import GenericField from '@/components/dynamic/form/templates/GenericField.vue';
import PositionInput from '@/components/forms/PositionInput.vue';
import DatePicker from '@/components/dynamic/form/components/DatePicker.vue';
import MobilePhoneInput from '@/components/dynamic/form/components/MobilePhoneInput.vue';

export default {
  name: 'Application',
  components: { GenericField, PositionInput, DatePicker, MobilePhoneInput },
  props: {
    formType: {
      type: String,
      default: 'application'
    },
    fields: {
      type: Array,
      default: () => []
    },
    application: {
      type: Object,
      default: () => ({})
    },
    title: {
      type: String,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    permission: {
      type: String,
      default: null
    },
    isCreateMode: {
      type: Boolean,
      default: false
    },
    hideExpandIcon: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      data: {},
      isEditMode: this.isCreateMode,
      isValidForm: false
    };
  },
  computed: {
    ...mapState(['references']),
    ...mapGetters(['canPerformAction']),
    collaboratorName () {
      return collaborator => `${collaborator.firstname} ${collaborator.lastname}`;
    },
    formatedDate () {
      return date => {
        if (!date) return '';

        return moment(date).format('DD-MM-YYYY');
      };
    }
  },
  watch: {
    application: {
      handler () {
        this.initializeData();
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    checkAllRequiredFieldsValid () {
      return this.fields.every(field => {
        if (['disabilityStatus', 'disability'].includes(field.name)) {
          if (!this.data.disabilityStatus) {
            return true;
          }
        }

        if (field.required || field.mandatory) {
          return !!this.data[field.name];
        }
        return true;
      });
    },
    setFormValidity () {
      if (this.$refs['application-form']) {
        const isValid = this.checkAllRequiredFieldsValid();

        this.isValidForm = isValid && this.$refs['application-form'].validate();
      }
    },
    handleChanges (field, value) {
      if (this.formType === 'application' && field.name === 'disabilityStatus') {
        const disabilityField = this.fields.find(f => f.name === 'disability');
        if (disabilityField) {
          this.$set(disabilityField, 'hidden', !value);
        }

        this.data.disability = this.data.disabilityStatus ? null : this.data.disability;
      }

      this.data[field.name] = value;
      this.setFormValidity();
    },
    setEditMode (value) {
      this.isEditMode = value;

      if (value) {
        this.$emit('is-edit-mode', value);
      }
    },
    saveChanges () {
      this.$emit('save-changes', this.data);

      this.setEditMode(false);
    },
    handleCancelAction () {
      this.setEditMode(false);
      this.initializeData();
    },
    getFormattedValue (field) {
      const value = field.computed
        ? this.getNestedValue(this.application, field.computed)
        : this.application[field.name];

      return this.formatFieldValue(field, value);
    },
    getNestedValue (obj, path) {
      return path
        .split('.')
        .reduce((acc, part) => (acc && acc[part]), obj);
    },
    initializeData () {
      const application = JSON.parse(JSON.stringify(this.application));

      this.fields.forEach(field => {
        this.$set(
          this.data,
          field.name,
          field.computed ? this.getNestedValue(application, field.computed) : application[field.name]
        );
      });
    },
    getFieldReferences (optionsByReference) {
      if (!optionsByReference) return [];

      return Object.entries(this.references[optionsByReference]).map(([key, value]) => ({ text: value, value: key }));
    },
    getInputClasses (field) {
      const classes = [field.mandatory ? 'required' : ''];

      if (this.isViewMode) classes.push('input-color-black');

      return classes.join(' ');
    },
    formatFieldValue (field, value) {
      if ([null, undefined, ''].includes(value)) {
        return 'N/A';
      }

      if (field.viewType === 'Switch') {
        return value ? 'Oui' : 'Non';
      }

      if (field.component === 'DatePicker') {
        return this.formatedDate(value);
      }

      // Handle Reference type
      if (field.optionsByReference) {
        return this.references[field.optionsByReference][value];
      }

      // Handle Computed Label
      if (field.computedLabel) {
        return this.getNestedValue(this.application, field.computedLabel);
      }

      return value;
    }
  }

};
</script>
